import type { DropDownListChangeEvent } from "@tql/ui-components-react/kendo/dropdowns";
import { Label } from "@tql/ui-components-react/kendo/labels";
import { DropDownList } from "@tql/ui-components-react/tql/dropdowns";
import { useAtom } from "jotai";

import { LoadingSpinner } from "~/shared/components/loading-spinner/loading-spinner.tsx";
import type { CustomerDetails } from "~/shared/schemas/customer-schema.ts";
import {
	availableCustomersAtom,
	selectedCustomerAtom,
} from "~/shared/state/user-profile-state.ts";

import { useGetUserCustomers } from "./hooks/use-get-user-customers.ts";
import { useSetActiveCustomerProfile } from "./hooks/use-set-active-customer-profile.ts";
import * as styles from "./index.module.css";

export const Profile = () => {
	const { isLoading } = useGetUserCustomers();
	const [availableCustomers] = useAtom(availableCustomersAtom);
	const [selectedCustomer, setSelectedCustomer] = useAtom(selectedCustomerAtom);
	const { mutateAsync: saveActiveProfile } = useSetActiveCustomerProfile();

	if (isLoading) {
		return <LoadingSpinner />;
	}

	const handleChange = ({ value }: { value: CustomerDetails | null }) => {
		if (!value) {
			// Never clear the active customer while the user still has accessible
			// customers — the atom drives external request headers and the app
			// expects a selection whenever one is available. Only reset to null
			// when there are genuinely no customers to select.
			if (availableCustomers.length === 0) {
				setSelectedCustomer(null);
			}
			return;
		}
		// Persist first and only commit to the atom on success. The atom drives
		// external request headers in fetch-client, so committing before a
		// confirmed save could drift the live session from the persisted profile.
		// On failure the mutation surfaces an error toast and the prior selection
		// is left untouched.
		void saveActiveProfile(value.id)
			.then(() => {
				setSelectedCustomer(value);
			})
			.catch(() => {
				// Error surfaced via the mutation's onError toast.
			});
	};

	return (
		<div className={styles.container}>
			<h1>User Profile</h1>
			<div className={styles.selectedCustomerInputWrapper}>
				<Label className={styles.selectedCustomerLabel}>
					Selected Customer
				</Label>
				<DropDownList
					className={styles.selectedCustomerInput}
					data={availableCustomers}
					dataItemKey={"id"}
					onChange={(e: DropDownListChangeEvent) =>
						handleChange({ value: e.value as CustomerDetails | null })
					}
					textField={"name"}
					value={selectedCustomer}
				/>
			</div>
		</div>
	);
};
